import { Link } from '@tanstack/react-router'
import type { MDXComponents } from 'mdx/types'
import React, { ComponentType } from 'react'
import { TweetProps } from 'react-tweet'
import { CTA } from '~/components/blog/CTA'
import { RevenueAnalysisChart } from '~/components/blog/RevenueAnalysisChart'
import { TestimonialCard } from '~/components/blog/TestimonialCard'
import ClientSideTweet from '~/components/ui/ClientSideTweet'
import { cn } from '~/lib/utils'

// Pre-create static MDX components object to avoid recreation on every call
const staticMDXComponents: MDXComponents = {
  // Custom components
  RevenueAnalysisChart,
  CTA,
  TestimonialCard,
  Image: ({ src, alt, width, height }: React.ImgHTMLAttributes<HTMLImageElement>) => {
    return <img src={src} alt={alt} width={width} height={height} className="border rounded-md border-border" />
  },
  Tweet: (tweetProps: TweetProps) => {
    return <ClientSideTweet id={tweetProps.id || ''} />
  },
  Separator: () => (
    <div className="my-12 h-px w-full bg-border dark:bg-border/50" />
  ),
  // Override native elements with enhanced versions
  h1: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h1
      className="mb-8 mt-12 font-display text-4xl leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl"
      {...props}
    />
  ),
  h2: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2
      className="mb-6 mt-10 font-display text-3xl leading-tight tracking-tight text-foreground md:text-4xl"
      {...props}
    />
  ),
  h3: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3
      className="mb-6 mt-16 font-display text-2xl leading-snug tracking-tight text-foreground md:text-3xl"
      {...props}
    />
  ),
  h4: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h4
      className="mb-3 mt-6 font-display text-xl leading-snug tracking-tight text-foreground md:text-2xl"
      {...props}
    />
  ),
  p: ({ ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p
      className="mb-4 text-base leading-relaxed text-foreground/80"
      {...props}
    />
  ),
  a: ({ href, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
    if (href && href.startsWith('/')) {
      return (
        <Link
          to={href}
          className="text-primary decoration-1 transition-all hover:text-primary/80 hover:decoration-2 hover:underline"
          {...props}
        />
      )
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary decoration-1 transition-all hover:text-primary/80 hover:decoration-2 hover:underline"
        {...props}
      />
    )
  },
  ul: (props: React.HTMLAttributes<HTMLUListElement>) => (
    <ul
      className="mb-6 list-disc space-y-2 pl-6 text-base text-foreground/80"
      {...props}
    />
  ),
  ol: (props: React.HTMLAttributes<HTMLOListElement>) => (
    <ol
      className="mb-6 list-decimal space-y-2 pl-6 text-base text-foreground/80"
      {...props}
    />
  ),
  li: (props: React.HTMLAttributes<HTMLLIElement>) => (
    <li className="text-base leading-relaxed text-foreground/80" {...props} />
  ),
  blockquote: (props: React.HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote
      className="my-8 border-l-4 border-primary/30 pl-6 text-lg italic leading-relaxed text-muted-foreground md:text-xl"
      {...props}
    />
  ),
  hr: () => <hr className="my-12 border-t border-border" />,
  img: ({ src, alt }: React.ImgHTMLAttributes<HTMLImageElement>) => {
    if (!src) return null
    const imgSrc = src.startsWith('/') ? src : `/${src}`
    return (
      <div className="relative my-10 h-auto w-full overflow-hidden rounded-md">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={imgSrc}
          alt={alt || 'Blog image'}
          width={1200}
          height={630}
          className="h-auto w-full rounded-md object-cover"
        />
        {alt && (
          <p className="mt-2 text-right text-xs text-muted-foreground">{alt}</p>
        )}
      </div>
    )
  },
  table: (props: React.TableHTMLAttributes<HTMLTableElement>) => (
    <div className="my-10 overflow-x-auto">
      <table
        className="w-full border-collapse rounded-lg border border-border text-base"
        {...props}
      />
    </div>
  ),
  thead: (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <thead className="bg-card" {...props} />
  ),
  th: (props: React.ThHTMLAttributes<HTMLTableCellElement>) => (
    <th
      className="border border-border p-4 text-left font-normal"
      {...props}
    />
  ),
  td: (props: React.TdHTMLAttributes<HTMLTableCellElement>) => (
    <td className="border border-border p-4" {...props} />
  ),
  pre: (props: React.HTMLAttributes<HTMLPreElement>) => (
    <pre
      className="my-8 overflow-x-auto rounded-lg bg-card/50 p-5 text-base will-change-auto"
      style={{
        contain: 'layout style paint',
        contentVisibility: 'auto',
        containIntrinsicSize: '0 200px',
        transform: 'translateZ(0)',
      }}
      {...props}
    />
  ),
  code: (props: React.HTMLAttributes<HTMLElement>) => (
    <code
      className="rounded bg-card/30 px-2 py-1 font-mono text-xs"
      {...props}
    />
  ),

  // Custom components
  Callout: ({
    children,
    type = 'info',
  }: {
    children: React.ReactNode
    type?: 'info' | 'warning' | 'error'
  }) => {
    const styles = {
      // Changed blue to gray/neutral to respect "no blue" request
      info: 'bg-gray-100 dark:bg-gray-900/50 border-gray-200 dark:border-gray-800',
      warning:
        'bg-yellow-100 dark:bg-yellow-950/50 border-yellow-200 dark:border-yellow-900',
      error:
        'bg-red-100 dark:bg-red-950/50 border-red-200 dark:border-red-900',
    }
    return (
      <div className={`${styles[type]} my-8 rounded-r-lg border-l-4 p-5`}>
        {children}
      </div>
    )
  },

  // Image grid component
  ImageGrid: ({ children }: { children: React.ReactNode }) => (
    <div className="my-10 grid grid-cols-1 gap-6 md:grid-cols-2">
      {children}
    </div>
  ),
  span: ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
    const colorClasses = {
      'text-primary': 'text-primary',
      'text-primary/80': 'text-primary/80',
      'text-primary/60': 'text-primary/60',
      'text-blue': 'text-blue-500',
      'text-blue-light': 'text-blue-300',
      'text-blue-dark': 'text-blue-700',
      'text-green': 'text-green-500',
      'text-green-light': 'text-green-300',
      'text-green-dark': 'text-green-700',
      'text-emerald': 'text-emerald-500',
      'text-red': 'text-red-500',
      'text-red-light': 'text-red-300',
      'text-red-dark': 'text-red-700',
      'text-rose': 'text-rose-500',
      'text-yellow': 'text-yellow-500',
      'text-yellow-light': 'text-yellow-300',
      'text-yellow-dark': 'text-yellow-700',
      'text-amber': 'text-amber-500',
      'text-purple': 'text-purple-500',
      'text-purple-light': 'text-purple-300',
      'text-purple-dark': 'text-purple-700',
      'text-violet': 'text-violet-500',
      'text-fuchsia': 'text-fuchsia-500',
      'text-pink': 'text-pink-500',
      'text-pink-light': 'text-pink-300',
      'text-pink-dark': 'text-pink-700',
      'text-orange': 'text-orange-500',
      'text-orange-light': 'text-orange-300',
      'text-orange-dark': 'text-orange-700',
      'text-teal': 'text-teal-500',
      'text-teal-light': 'text-teal-300',
      'text-teal-dark': 'text-teal-700',
      'text-cyan': 'text-cyan-500',
      'text-cyan-light': 'text-cyan-300',
      'text-cyan-dark': 'text-cyan-700',
      'text-indigo': 'text-indigo-500',
      'text-indigo-light': 'text-indigo-300',
      'text-indigo-dark': 'text-indigo-700',
      'text-gray': 'text-gray-500',
      'text-gray-light': 'text-gray-300',
      'text-gray-dark': 'text-gray-700',
      'text-slate': 'text-slate-500',
      'text-slate-light': 'text-slate-300',
      'text-slate-dark': 'text-slate-700',
      'text-zinc': 'text-zinc-500',
      'text-zinc-light': 'text-zinc-300',
      'text-zinc-dark': 'text-zinc-700',
      'text-neutral': 'text-neutral-500',
      'text-neutral-light': 'text-neutral-300',
      'text-neutral-dark': 'text-neutral-700',
      'text-stone': 'text-stone-500',
      'text-stone-light': 'text-stone-300',
      'text-stone-dark': 'text-stone-700',
    }
    const colorClass = Object.keys(colorClasses).find((key) =>
      className?.includes(key),
    )
    return (
      <span
        className={cn(
          colorClass
            ? colorClasses[colorClass as keyof typeof colorClasses]
            : '',
          className,
        )}
        {...props}
      />
    )
  },
}

// Define components for MDX - now returns static object with merged components
export const mdxComponents: MDXComponents = new Proxy(staticMDXComponents, {
  get(target, prop: string) {
    if (prop in target) return target[prop as keyof typeof target]
    
    // Helper to create unknown component
    const createUnknownComponent = (name: string): ComponentType<any> => {
        const Unknown = (props: any) => {
        if (typeof console !== 'undefined') {
            // eslint-disable-next-line no-console
            console.warn(`[MDX] Unknown component "${name}". Rendering children without special handling.`)
        }
        const { children, ...rest } = props ?? {}
        return <div {...rest}>{children}</div>
        }
        Unknown.displayName = `UnknownMdx(${name})`
        return Unknown
    }

    return createUnknownComponent(prop)
  },
}) as MDXComponents
